import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

type ConfirmationDialogOptions = {
  /** Opting in is a contract: pass `confirmPending` to the dialog so it locks its exits, make sure `onConfirm`
   * settles, and report a rejection yourself — the hook only closes the dialog. */
  readonly holdWhilePending?: boolean;
};

function useConfirmationDialog(
  onConfirm: () => unknown,
  { holdWhilePending = false }: ConfirmationDialogOptions = {}
) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMounted, setConfirmMounted] = useState(false);
  const [confirmPending, setConfirmPending] = useState(false);

  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleConfirmHeld = useCallback(async () => {
    setConfirmPending(true);
    try {
      await onConfirm();
    } catch {
      // Reporting the failure is the caller's; this only stops an unhandled rejection.
    }
    if (!mountedRef.current) return;
    setConfirmPending(false);
    setConfirmOpen(false);
  }, [onConfirm]);

  const handleConfirm = useCallback(() => {
    if (confirmPending) return undefined;
    if (holdWhilePending) return handleConfirmHeld();
    setConfirmOpen(false);
    onConfirm();
    return undefined;
  }, [confirmPending, handleConfirmHeld, holdWhilePending, onConfirm]);

  const handleCancel = useCallback(() => {
    if (!confirmPending) setConfirmOpen(false);
  }, [confirmPending]);
  const handleExited = useCallback(() => setConfirmMounted(false), []);

  const handleAction = useCallback(() => {
    setConfirmOpen(true);
    setConfirmMounted(true);
  }, []);

  return useMemo(() => ({
    confirmOpen,
    confirmMounted,
    confirmPending,
    handleAction,
    handleCancel,
    handleExited,
    handleConfirm
  }), [confirmMounted, confirmOpen, confirmPending, handleAction, handleCancel, handleConfirm, handleExited]);
}

export default useConfirmationDialog;
